import { useEffect, useRef } from 'react';

const AnalyticsTracker = () => {
  const visitorId = useRef(localStorage.getItem('visitor_id') || `v_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const sessionId = useRef(sessionStorage.getItem('session_id') || `s_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const hasTrackedPageview = useRef(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    // Save IDs
    localStorage.setItem('visitor_id', visitorId.current);
    sessionStorage.setItem('session_id', sessionId.current);

    // Track page view (only once per page load)
    if (!hasTrackedPageview.current) {
      hasTrackedPageview.current = true;
      trackPageView();
    }

    // Track clicks
    document.addEventListener('click', handleClick);
    
    // Heartbeat for live visitors
    const heartbeatInterval = setInterval(sendHeartbeat, 30000);

    return () => {
      document.removeEventListener('click', handleClick);
      clearInterval(heartbeatInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendHeartbeat = async () => {
    try {
      await fetch(`${backendUrl}/api/analytics/heartbeat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          visitor_id: visitorId.current,
          page: window.location.pathname
        })
      });
    } catch (error) {
      // Silent fail
    }
  };

  const trackPageView = async () => {
    try {
      await fetch(`${backendUrl}/api/analytics/pageview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          page: window.location.pathname,
          visitor_id: visitorId.current,
          session_id: sessionId.current,
          referrer: document.referrer
        })
      });
    } catch (error) {
      // Silent fail
    }
  };

  const handleClick = async (e) => {
    // Track link clicks
    const link = e.target.closest('a');
    if (link) {
      const href = link.getAttribute('href');
      if (!href) return;
      
      const linkType = getLinkType(href, link);
      const linkName = getLinkName(link, href);
      const category = getCTACategory(href, link);

      try {
        await fetch(`${backendUrl}/api/analytics/click`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            link_type: linkType,
            link_name: linkName,
            link_url: href,
            visitor_id: visitorId.current,
            referrer: document.referrer,
            page: window.location.pathname,
            category: category
          })
        });
      } catch (error) {
        // Silent fail
      }
    }
  };

  const getLinkType = (href, link) => {
    if (!href) return 'other';
    if (href.includes('instagram.com')) return 'social';
    if (href.includes('linkedin.com')) return 'social';
    if (href.includes('youtube.com')) return 'social';
    if (href.includes('twitter.com') || href.includes('x.com')) return 'social';
    if (href.startsWith('mailto:')) return 'social';
    if (href.includes('airtable.com')) return 'cta';
    if (href.includes('datadreaming.cloud')) return 'cta';
    if (href.includes('beehiiv.com')) return 'cta';
    if (link.textContent?.toLowerCase().includes('work with me')) return 'cta';
    if (link.textContent?.toLowerCase().includes('get access')) return 'cta';
    if (href.startsWith('#') || href.startsWith('/')) return 'navigation';
    return 'external';
  };

  const getCTACategory = (href, link) => {
    const text = link.textContent?.toLowerCase() || '';
    const hrefLower = href.toLowerCase();
    
    // Work With Me
    if (hrefLower.includes('airtable.com') || text.includes('work with me')) {
      return 'work_with_me';
    }
    
    // Data Dreaming
    if (hrefLower.includes('datadreaming.cloud')) {
      return 'data_dreaming_site';
    }
    if (hrefLower.includes('beehiiv.com') || text.includes('newsletter') || text.includes('sign up')) {
      return 'data_dreaming_newsletter';
    }
    
    // SIP Sessions / YouTube
    if (hrefLower.includes('youtube.com') || hrefLower.includes('youtu.be')) {
      return 'sip_sessions';
    }
    
    // Socials
    if (hrefLower.includes('instagram.com')) return 'social_instagram';
    if (hrefLower.includes('linkedin.com')) return 'social_linkedin';
    if (hrefLower.includes('twitter.com') || hrefLower.includes('x.com')) return 'social_twitter';
    if (hrefLower.startsWith('mailto:')) return 'social_email';
    
    return 'other';
  };

  const getLinkName = (link, href) => {
    // Check for specific platforms/CTAs
    if (href.includes('instagram.com')) return 'Instagram';
    if (href.includes('linkedin.com')) return 'LinkedIn';
    if (href.includes('youtube.com') || href.includes('youtu.be')) return 'YouTube (SIP Sessions)';
    if (href.includes('twitter.com') || href.includes('x.com')) return 'Twitter/X';
    if (href.startsWith('mailto:')) return 'Email';
    if (href.includes('airtable.com')) return 'Work With Me';
    if (href.includes('datadreaming.cloud')) return 'Data Dreaming Site';
    if (href.includes('beehiiv.com')) return 'Newsletter Signup';
    
    // Check for aria-label
    if (link.getAttribute('aria-label')) return link.getAttribute('aria-label');
    
    // Check for text content
    const text = link.textContent?.trim();
    if (text && text.length > 0 && text.length < 60) return text;
    
    // Check for title
    if (link.getAttribute('title')) return link.getAttribute('title');
    
    // Check for child image alt
    const img = link.querySelector('img');
    if (img?.alt) return img.alt;
    
    // Extract domain from URL
    try {
      const url = new URL(href, window.location.origin);
      if (url.hostname && url.hostname !== window.location.hostname) {
        return url.hostname.replace('www.', '');
      }
    } catch (e) {
      // Invalid URL
    }
    
    return href.slice(0, 50);
  };

  return null;
};

export default AnalyticsTracker;
